import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "selector-estructura" }
const _hoisted_2 = { class: "instruccion" }
const _hoisted_3 = { class: "filtro-botones" }
const _hoisted_4 = {
  key: 0,
  class: "filtro-select-wrap"
}
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "arbol-contenedor" }
const _hoisted_7 = ["draggable", "onDragstart", "onDragover", "onDrop"]
const _hoisted_8 = ["title", "onClick"]
const _hoisted_9 = {
  key: 0,
  class: "sin-datos"
}

import { ref, computed, onMounted } from 'vue'
import { useComponentes } from '@/store/GestionComponentes'
import useModelo from '@/store/GestionModelo'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import type Componente from '@/interfaces/Componente'

interface NodoPlano {
  tipo: 'componente'
  nivel: number
  nombre: string
  compId: number
  ordinal: string
  tieneHijos: boolean
  rootCompId: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectorEstructuraArbol',
  props: {
    idModelo: {}
  },
  emits: ["update:cargando"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

/**
 * Selector de nodos del árbol, para filtrar o clonar por ordinal.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const props = __props

const emit = __emit

const componentesStore = useComponentes()
const modeloStore = useModelo()

const arbol = ref<Componente[]>([])
const nivelesArray = ref<string[]>([])
const excluidos = ref(new Set<number>())
const idRaizComp = ref<number | null>(null)
const tipoFiltro = ref<'todos' | 'por-componente'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)
const draggingCompId = ref<number | null>(null)
const dropTargetCompId = ref<number | null>(null)
const dropSide = ref<'before' | 'after'>('after')

function obtenerNombreNivel(nodo: NodoPlano): string {
  return nivelesArray.value[nodo.nivel] ?? `Nivel ${nodo.nivel + 1}`
}

const nodosPlanos = computed<NodoPlano[]>(() => {
  const result: NodoPlano[] = []

  function aplanar(comps: Componente[], nivel: number, rootCompId?: number) {
    const sorted = nivel === 0
      ? [...comps]
      : [...comps].sort((a, b) =>
          (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
        )
    for (const comp of sorted) {
      const tieneHijos = (comp.children?.length ?? 0) > 0
      const thisRootId = nivel === 0 ? comp.id_componente! : rootCompId!
      result.push({
        tipo: 'componente',
        nivel,
        nombre: comp.nombre,
        compId: comp.id_componente!,
        ordinal: comp.ordinal ?? '',
        tieneHijos,
        rootCompId: thisRootId,
      })
      if (tieneHijos) aplanar(comp.children!, nivel + 1, thisRootId)
    }
  }

  aplanar(arbol.value, 0)
  return result
})

const componentesNivel1Sorted = computed(() =>
  [...arbol.value].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const nodosVisuales = computed<NodoPlano[]>(() => {
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    return nodosPlanos.value.filter(n => n.rootCompId === componenteNivel1Filtro.value)
  }
  return nodosPlanos.value
})

function onDragStart(compId: number, e: DragEvent) {
  draggingCompId.value = compId
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
}

function onDragOver(compId: number, e: DragEvent) {
  if (draggingCompId.value == null || draggingCompId.value === compId) return
  dropTargetCompId.value = compId
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dropSide.value = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
}

function onDragEnd() {
  draggingCompId.value = null
  dropTargetCompId.value = null
}

function onDrop(compId: number) {
  if (draggingCompId.value == null || draggingCompId.value === compId) {
    draggingCompId.value = null
    dropTargetCompId.value = null
    return
  }
  const draggedId = draggingCompId.value
  const side = dropSide.value
  draggingCompId.value = null
  dropTargetCompId.value = null

  const arr = [...arbol.value]
  const draggedIdx = arr.findIndex(c => c.id_componente === draggedId)
  const targetIdx = arr.findIndex(c => c.id_componente === compId)
  if (draggedIdx === -1 || targetIdx === -1) return

  const [draggedComp] = arr.splice(draggedIdx, 1)
  const newTargetIdx = arr.findIndex(c => c.id_componente === compId)
  arr.splice(side === 'before' ? newTargetIdx : newTargetIdx + 1, 0, draggedComp)
  arbol.value = arr
}

function estaExcluido(nodo: NodoPlano): boolean {
  return excluidos.value.has(nodo.compId)
}

function estiloNodo(nodo: NodoPlano): Record<string, string> {
  if (estaExcluido(nodo)) {
    const grosor = nodo.tieneHijos ? '5px' : '3px'
    return {
      backgroundColor: '#ffd5d5',
      borderLeft: `${grosor} solid #cc2200`,
      boxShadow: '0 1px 4px rgba(204,34,0,0.15)',
    }
  }
  const claridad = Math.min(97, 85 + nodo.nivel * 8)
  return {
    backgroundColor: `hsl(210, 60%, ${claridad}%)`,
    borderLeft: nodo.tieneHijos ? '5px solid #0e69a0' : '3px solid #8bb8d6',
    boxShadow: nodo.tieneHijos
      ? '0 2px 6px rgba(0,0,0,0.12)'
      : '0 1px 3px rgba(0,0,0,0.07)',
  }
}

function todosDescIds(comp: Componente): number[] {
  const ids: number[] = [comp.id_componente!]
  for (const h of (comp.children ?? [])) ids.push(...todosDescIds(h))
  return ids
}

function encontrarComp(id: number, lista: Componente[] = arbol.value): Componente | null {
  for (const c of lista) {
    if (c.id_componente === id) return c
    const found = encontrarComp(id, c.children ?? [])
    if (found) return found
  }
  return null
}

function toggleNodo(nodo: NodoPlano) {
  const comp = encontrarComp(nodo.compId)
  if (!comp) return
  const next = new Set(excluidos.value)
  const descIds = todosDescIds(comp)
  if (next.has(comp.id_componente!)) {
    descIds.forEach(id => next.delete(id))
  } else {
    descIds.forEach(id => next.add(id))
  }
  excluidos.value = next
}

async function cargarEstructura() {
  if (!props.idModelo) return
  emit('update:cargando', true)
  try {
    const [raiz, modelo] = await Promise.all([
      componentesStore.cargarRaizPorModelo(props.idModelo),
      modeloStore.getModeloById(props.idModelo),
    ])
    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)
    if (!raiz?.id_componente) return
    idRaizComp.value = raiz.id_componente
    await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
    const raizCompleta = componentesStore.componentes[0]
    arbol.value = [...(raizCompleta?.children ?? [])].sort((a, b) =>
      (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
    )
  } finally {
    emit('update:cargando', false)
  }
}

// Devuelve true si algún descendiente directo o indirecto está excluido
function tieneDescendienteExcluido(comp: Componente): boolean {
  for (const hijo of (comp.children ?? [])) {
    if (excluidos.value.has(hijo.id_componente!)) return true
    if (tieneDescendienteExcluido(hijo)) return true
  }
  return false
}

// Recoge el conjunto MÍNIMO de ordinales a enviar al backend.
// Si un componente y todos sus hijos están incluidos → se envía su ordinal (no se baja más).
// Si tiene algún descendiente excluido → se baja a sus hijos y se repite.
// Si él mismo está excluido → se omite por completo.
function colectarOrdinalesIncluidos(): string[] {
  const result: string[] = []

  function recolectar(comps: Componente[]): boolean {
    let aportaAlgo = false
    for (const comp of comps) {
      if (excluidos.value.has(comp.id_componente!)) continue
      if (tieneDescendienteExcluido(comp)) {
        const hijosAportaron = recolectar(comp.children ?? [])
        if (!hijosAportaron) {
          if (comp.ordinal) { result.push(comp.ordinal); aportaAlgo = true }
        } else {
          aportaAlgo = true
        }
      } else {
        if (comp.ordinal) { result.push(comp.ordinal); aportaAlgo = true }
      }
    }
    return aportaAlgo
  }

  recolectar(arbol.value)
  return result
}

// La estructura se carga al montar; el componente se monta al entrar al paso de selección.
onMounted(cargarEstructura)

// Expuesto al modal contenedor para que recoja la selección al confirmar.
__expose({ obtenerOrdinales: colectarOrdinalesIncluidos })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("p", _hoisted_2, [
      _cache[4] || (_cache[4] = _createTextVNode(" Pulse la ")),
      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "x-ejemplo" }, "✕", -1)),
      _cache[6] || (_cache[6] = _createTextVNode(" del elemento que ")),
      _cache[7] || (_cache[7] = _createElementVNode("strong", null, "NO desea incluir", -1)),
      _cache[8] || (_cache[8] = _createTextVNode(". Al deshabilitar un componente también se deshabilitan sus hijos. Arrastre el ícono ")),
      _cache[9] || (_cache[9] = _createElementVNode("strong", null, "⠿", -1)),
      _createTextVNode(" para cambiar el orden de los " + _toDisplayString(nivelesArray.value[0]?.toLowerCase() ?? 'componentes') + " de primer nivel. ", 1)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("button", {
        class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'todos' ? 'activo' : '']),
        onClick: _cache[0] || (_cache[0] = ($event: any) => {tipoFiltro.value = 'todos'; componenteNivel1Filtro.value = null})
      }, "Mostrar todo", 2),
      _createElementVNode("button", {
        class: _normalizeClass(['btn-filtro', tipoFiltro.value === 'por-componente' ? 'activo' : '']),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (tipoFiltro.value = 'por-componente'))
      }, "Por " + _toDisplayString(nivelesArray.value[0] ?? 'Componente'), 3)
    ]),
    (tipoFiltro.value === 'por-componente')
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((componenteNivel1Filtro).value = $event)),
            class: "filtro-select-ctrl"
          }, [
            _cache[10] || (_cache[10] = _createElementVNode("option", { value: null }, "— Selecciona —", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentesNivel1Sorted.value, (c) => {
              return (_openBlock(), _createElementBlock("option", {
                key: c.id_componente,
                value: c.id_componente
              }, _toDisplayString(c.ordinal) + " – " + _toDisplayString(c.nombre), 9, _hoisted_5))
            }), 128))
          ], 512), [
            [
              _vModelSelect,
              componenteNivel1Filtro.value,
              void 0,
              { number: true }
            ]
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nodosVisuales.value, (nodo) => {
        return (_openBlock(), _createElementBlock("div", {
          key: `comp-${nodo.compId}`,
          class: _normalizeClass(["nodo-fila nodo-componente", {
          'nodo-excluido': estaExcluido(nodo),
          'nodo-arrastrando': nodo.nivel === 0 && draggingCompId.value === nodo.compId,
          'drop-before': nodo.nivel === 0 && dropTargetCompId.value === nodo.compId && dropSide.value === 'before',
          'drop-after': nodo.nivel === 0 && dropTargetCompId.value === nodo.compId && dropSide.value === 'after',
        }]),
          style: _normalizeStyle(estiloNodo(nodo)),
          draggable: nodo.nivel === 0 && arbol.value.length > 1,
          onDragstart: _withModifiers(($event: any) => (nodo.nivel === 0 && onDragStart(nodo.compId, $event)), ["stop"]),
          onDragover: _withModifiers(($event: any) => (nodo.nivel === 0 && onDragOver(nodo.compId, $event)), ["stop","prevent"]),
          onDragend: onDragEnd,
          onDrop: _withModifiers(($event: any) => (nodo.nivel === 0 && onDrop(nodo.compId)), ["stop","prevent"])
        }, [
          _createElementVNode("span", {
            class: _normalizeClass(["nivel-label", { 'nivel-excluido': estaExcluido(nodo) }])
          }, _toDisplayString(obtenerNombreNivel(nodo)), 3),
          _createElementVNode("span", {
            class: "nodo-nombre",
            style: _normalizeStyle({ paddingLeft: `${nodo.nivel * 20}px` })
          }, [
            (nodo.nivel === 0 && arbol.value.length > 1)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  class: "drag-handle",
                  title: "Arrastra para reordenar",
                  onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                }, "⠿"))
              : _createCommentVNode("", true),
            _createTextVNode(_toDisplayString(nodo.nombre), 1)
          ], 4),
          _createElementVNode("button", {
            class: _normalizeClass(["btn-excluir", { excluido: estaExcluido(nodo) }]),
            title: estaExcluido(nodo) ? 'Incluir este elemento' : 'Excluir este elemento',
            onClick: _withModifiers(($event: any) => (toggleNodo(nodo)), ["stop"])
          }, "✕", 10, _hoisted_8)
        ], 46, _hoisted_7))
      }), 128)),
      (nodosVisuales.value.length === 0)
        ? (_openBlock(), _createElementBlock("p", _hoisted_9, " No se encontraron componentes en este modelo. "))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})